
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { IndicesType } from '@/store/enums/TypeOfIndicesEnums';
  import { IndexTypeViewTypeObject } from '@/store/modules/Indices/IndicesAssetsModule';
  const selectedGov = ref<number>(0);

  export default defineComponent({
    name: 'areas-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const isPriceIndex = ref(false);
      const isVolumeIndex = ref(false);
      const supportedCategories = ref<number[]>([]);
      const viewTypesByIndexType = ref<
        Record<number, IndexTypeViewTypeObject[]>
      >({
        [IndicesType.PRICE_INDEX.id]: [],
        [IndicesType.VOLUME_INDEX.id]: [],
      });
      const viewAvailabilities = ref<Record<number, boolean>>({});

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const areaSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        values.isRegisteredTrades =
          values.isRegisteredTrades == true ? true : false;
        values.isAd = values.isAd == true ? true : false;
        values.isAuction = values.isAuction == true ? true : false;

        const allViewTypes = (
          viewTypesByIndexType.value[IndicesType.PRICE_INDEX.id] ?? []
        ).concat(viewTypesByIndexType.value[IndicesType.VOLUME_INDEX.id] ?? []);
        const slugToActive = Object.fromEntries(
          allViewTypes.map((vt) => [
            vt.slug,
            viewAvailabilities.value[vt.id] ?? false,
          ])
        );

        values.area_index_categories = supportedCategories.value.map(
          (categoryId) => ({
            category_id: categoryId,
            price_index_active: isPriceIndex.value,
            volume_index_active: isVolumeIndex.value,
            price_index_view_active: slugToActive['price-index'] ?? false,
            ppm_view_active: slugToActive['price-per-meter'] ?? false,
            volume_index_view_active: slugToActive['volume-index'] ?? false,
            no_of_deals_view_active:
              slugToActive['total-transactions'] ?? false,
          })
        );

        await store.dispatch(Actions.CREATE_AREA, values);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_AREA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'areas-listing' });
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('areasCreating'), [
          translate('assets'),
        ]);
      });
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      await store.dispatch(Actions.GET_ALL_GOV_LIST);
      await store.dispatch(Actions.GET_ALL_INDICES_PROPERTY_CATEGORIES);
      const [priceVTs, volumeVTs] = await Promise.all([
        store.dispatch(
          Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
          IndicesType.PRICE_INDEX.id
        ),
        store.dispatch(
          Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
          IndicesType.VOLUME_INDEX.id
        ),
      ]);
      viewTypesByIndexType.value = {
        [IndicesType.PRICE_INDEX.id]: priceVTs ?? [],
        [IndicesType.VOLUME_INDEX.id]: volumeVTs ?? [],
      };
      viewAvailabilities.value = Object.fromEntries(
        (priceVTs ?? []).concat(volumeVTs ?? []).map((vt) => [vt.id, true])
      );

      return {
        selectedGov,
        govs: computed(() => store.getters.allListGov),
        indicesPropertyCategories: computed(
          () => store.getters.allListIndicesPropertyCategories
        ),
        onSubmitCreate,
        submitButton,
        translate,
        areaSchema,
        goBack,
        can,
        IndicesType,
        isPriceIndex,
        isVolumeIndex,
        viewTypesByIndexType,
        viewAvailabilities,
        supportedCategories,
      };
    },
  });
